import type { CustomAttributeData } from '@risksmart-app/domain/src/types/common.types';
import type { UpdateDocumentInternalAuditResultMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

export type UpdateDocumentInternalAuditResultInput = {
  Id: string;
  Rating?: number | null;
  Rationale?: string | null;
  TestDate?: string | null;
  CustomAttributeData?: CustomAttributeData | null;
};

// The tRPC update returns the updated Id; the async mutation always affects the
// single targeted row, so we report affected_rows: 1 to preserve the GraphQL
// consumer's `affected_rows === 0` failure check.
const mapTrpcResponseToGraphQL =
  (): UpdateDocumentInternalAuditResultMutation => ({
    update_document_internal_audit_result: {
      __typename: 'document_internal_audit_result_mutation_response',
      affected_rows: 1,
    },
  });

export const useUpdateDocumentInternalAuditResultTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...trpc.frontend.internalAuditResult.updateDocument.mutationOptions({
      onSuccess: async () => {
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.internalAuditResult.pathKey(),
          }),
          queryClient.invalidateQueries({
            queryKey:
              trpc.frontend.assessment.internalAuditResultCount.queryKey(),
          }),
          // COI-471 parity: Apollo twin also evicts `internal_audit_report`.
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.internalAuditReport.pathKey(),
          }),
        ]);
      },
    }),
  });

  return {
    updateDocumentInternalAuditResult: async (
      variables: UpdateDocumentInternalAuditResultInput
    ): Promise<UpdateDocumentInternalAuditResultMutation> => {
      await mutation.mutateAsync(variables);

      return mapTrpcResponseToGraphQL();
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
